import { IApiError } from '@frontend/request';

import { accountAPIController } from '@aqx-core/common/api/account';
import { EOTPType, authenticationAPIController } from '@aqx-core/common/api/auth';
import { ECrmType } from '@aqx-core/common/api/user-account';
import { ECommonResponseCode, mapResponseCodeToMessage } from '@aqx-core/common/request';

import { signUpCrmService } from './service';
import { ESignUpCrmContent, ICompleteBasicInfoStepParams, ICompleteSignUpParams, IRequestOtpParams } from './types';

// Hardcode crmType & clientPortalUrl to stoneinno
const CRM_TYPE = ECrmType.STONEINNO;

class SignUpCrmViewController {
  public reset = () => {
    signUpCrmService.reset();
  };

  public getCountries = async (onError: (message: string) => void) => {
    signUpCrmService.setLoading(true);
    await accountAPIController
      .getStoneinnoCountries({
        onError: (error) => {
          const { code, message } = error as IApiError;
          onError(mapResponseCodeToMessage(code, message));
        },
        onSuccess: (res) => {
          if (res) {
            signUpCrmService.setCountries(res);
          }
        }
      })
      .finally(() => {
        signUpCrmService.setLoading(false);
      });
  };

  public completeBasicInfoStep = async ({ basicInfo, onError, onSuccess }: ICompleteBasicInfoStepParams) => {
    signUpCrmService.setBasicInfo(basicInfo);
    const onFetchOtpSuccess = () => {
      signUpCrmService.setContent(ESignUpCrmContent.VERIFICATION);
      onSuccess();
    };

    await this.requestOtp({ onError, onSuccess: onFetchOtpSuccess, isBasicInfoStep: true });
  };

  public requestOtp = async ({ onSuccess, onError, isBasicInfoStep }: IRequestOtpParams) => {
    signUpCrmService.setLoading(true);
    const { email } = signUpCrmService.context.basicInfo || {};
    if (!email) {
      signUpCrmService.setLoading(false);
      return;
    }
    await authenticationAPIController
      .requestCrmOtp(
        { email, crmType: CRM_TYPE, type: EOTPType.EMAIL },
        {
          onSuccess,
          onError: (error) => {
            const { code, message } = error as IApiError;
            // If verfication cool down upon completing basic info step, proceed to navigate user to the next step
            if (isBasicInfoStep && code === ECommonResponseCode.SI_1016) {
              onSuccess();
            }
            onError(mapResponseCodeToMessage(code, message), code);
          }
        }
      )
      .finally(() => {
        signUpCrmService.setLoading(false);
      });
  };

  public completeSignUp = async ({ params, onError }: ICompleteSignUpParams) => {
    const { basicInfo } = signUpCrmService.context;
    if (!basicInfo) {
      return;
    }
    signUpCrmService.setLoading(true);
    await authenticationAPIController
      .signUpCrm(
        { ...params, ...basicInfo, crmType: CRM_TYPE },
        {
          onSuccess: (result) => {
            signUpCrmService.setContent(ESignUpCrmContent.COMPLETE);
            if (result) {
              signUpCrmService.setRedirectUrl(result);
            }
          },
          onError: (error) => {
            const { code, message } = error as IApiError;
            onError(mapResponseCodeToMessage(code, message));
          }
        }
      )
      .finally(() => {
        signUpCrmService.setLoading(false);
      });
  };
}

export const signUpCrmViewController = new SignUpCrmViewController();
